import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Modal from '../components/02_Modal.js';
import projectsData from '../data/projectsData.json';

const HomeRetroDual = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedProject(null), 300);
  };

  // Get projects marked as featured in the JSON
  const featuredProjects = projectsData.filter(project => project.featured === true);

  const skills = [
    { name: "CAD Design", icon: "📐", color: "bg-blue-400 dark:bg-cyan-400" },
    { name: "Manufacturing", icon: "⚙️", color: "bg-yellow-400 dark:bg-pink-400" },
    { name: "Programming", icon: "💻", color: "bg-green-400 dark:bg-yellow-400" }
  ];

  return (
    <div className="min-h-screen bg-[#F5E6D3] dark:bg-[#1a1a2e] relative overflow-hidden transition-colors duration-500">
      {/* Dot Grid Background */}
      <div 
        className="absolute inset-0 opacity-20 dark:opacity-30 transition-opacity duration-500"
        style={{
          backgroundImage: `radial-gradient(circle, #8B7355 1px, transparent 1px)`,
          backgroundSize: '24px 24px'
        }}
      />
      
      {/* Dark Mode: Glowing Dot Grid */}
      <div 
        className="absolute inset-0 opacity-0 dark:opacity-30 transition-opacity duration-500"
        style={{
          backgroundImage: `radial-gradient(circle, #00ff88 1px, transparent 1px)`,
          backgroundSize: '24px 24px'
        }}
      />

      {/* Dark Mode: Scanline Effect */}
      <div 
        className="absolute inset-0 opacity-0 dark:opacity-5 pointer-events-none transition-opacity duration-500"
        style={{
          backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 2px, #00ff88 2px, #00ff88 4px)`
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 py-12">
        
        {/* Hero Window */}
        <motion.div
          className="bg-[#FFF8E7] dark:bg-[#2d2d44] border-4 border-black dark:border-cyan-400 rounded-none shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[8px_8px_0px_0px_rgba(0,255,136,0.5)] mb-8 transition-all duration-500"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Window Title Bar */}
          <div className="bg-gradient-to-r from-orange-400 to-red-400 dark:from-pink-500 dark:to-purple-600 border-b-4 border-black dark:border-cyan-400 px-4 py-3 flex items-center justify-between transition-all duration-500">
            <div className="flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-4 h-4 rounded-full bg-red-500 border-2 border-black dark:shadow-[0_0_10px_rgba(255,0,0,0.8)]"></div>
                <div className="w-4 h-4 rounded-full bg-yellow-300 border-2 border-black dark:shadow-[0_0_10px_rgba(255,255,0,0.8)]"></div>
                <div className="w-4 h-4 rounded-full bg-green-400 border-2 border-black dark:shadow-[0_0_10px_rgba(0,255,0,0.8)]"></div>
              </div>
              <span className="font-black text-lg uppercase tracking-tight ml-2 dark:text-cyan-300 transition-colors duration-500">WELCOME.exe</span>
            </div>
            <div className="flex gap-2">
              <button className="w-6 h-6 bg-white dark:bg-[#2d2d44] border-2 border-black dark:border-cyan-400 flex items-center justify-center font-black text-xs hover:bg-gray-200 dark:hover:bg-cyan-400 dark:hover:text-black dark:text-cyan-300 transition-all duration-300">_</button>
              <button className="w-6 h-6 bg-white dark:bg-[#2d2d44] border-2 border-black dark:border-cyan-400 flex items-center justify-center font-black text-xs hover:bg-gray-200 dark:hover:bg-cyan-400 dark:hover:text-black dark:text-cyan-300 transition-all duration-300">□</button>
              <button className="w-6 h-6 bg-white dark:bg-[#2d2d44] border-2 border-black dark:border-cyan-400 flex items-center justify-center font-black text-xs hover:bg-gray-200 dark:hover:bg-cyan-400 dark:hover:text-black dark:text-cyan-300 transition-all duration-300">✕</button>
            </div>
          </div>

          {/* Window Content */}
          <div className="p-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-center mb-8"
            >
              <h1 className="text-6xl font-black text-black dark:text-white uppercase tracking-tight leading-none mb-4 transition-colors duration-500" style={{ fontFamily: "'Arial Black', sans-serif" }}>
                HI, I'M<br/>
                <span className="text-blue-600 dark:text-cyan-400 dark:drop-shadow-[0_0_20px_rgba(0,255,255,0.8)] transition-all duration-500">CONNOR</span>{" "}
                <span className="text-orange-500 dark:text-yellow-400 dark:drop-shadow-[0_0_20px_rgba(255,255,0,0.8)] transition-all duration-500">MADIGAN</span>
              </h1>
              
              <div className="inline-block bg-black dark:bg-black text-white dark:text-green-400 px-6 py-3 border-4 border-black dark:border-green-400 transform -rotate-1 mb-6 dark:shadow-[0_0_20px_rgba(0,255,0,0.5)] transition-all duration-500">
                <p className="text-xl font-bold uppercase tracking-wide dark:font-mono">
                  <span className="dark:hidden">Mechanical Engineer | CAD Designer | Problem Solver</span>
                  <span className="hidden dark:inline">&gt; MECHANICAL ENGINEER | CAD DESIGNER | PROBLEM SOLVER_</span>
                </p>
              </div>

              {/* Skills Pills */}
              <div className="flex justify-center gap-4 flex-wrap mt-8">
                {skills.map((skill, index) => (
                  <motion.div
                    key={skill.name}
                    className={`${skill.color} border-4 border-black px-6 py-3 transform hover:scale-105 transition-transform cursor-pointer shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_15px_currentColor] dark:border-black`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    whileHover={{ rotate: -2 }}
                  >
                    <span className="text-3xl mr-2">{skill.icon}</span>
                    <span className="font-black text-lg uppercase tracking-tight text-black dark:text-black">{skill.name}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Featured Projects Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <div className="bg-white dark:bg-[#2d2d44] border-4 border-black dark:border-pink-400 px-4 py-2 inline-block shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_20px_rgba(255,0,255,0.5)] transition-all duration-500">
              <h2 className="text-3xl font-black uppercase tracking-tight dark:text-pink-400 transition-colors duration-500">Featured Projects</h2>
            </div>
            
            <Link
              to="/04_projects"
              className="bg-purple-400 dark:bg-cyan-400 border-4 border-black dark:border-cyan-400 px-6 py-3 font-black uppercase text-sm hover:bg-purple-500 dark:hover:bg-cyan-500 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_15px_rgba(0,255,255,0.6)] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] dark:hover:shadow-[0_0_20px_rgba(0,255,255,0.8)] transition-all hover:translate-x-[-2px] hover:translate-y-[-2px] text-black inline-flex items-center gap-2 duration-500"
            >
              VIEW ALL PROJECTS <span className="text-lg">→</span>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {featuredProjects.map((project, index) => {
              const colorOptions = [
                { bg: 'bg-yellow-400', border: 'border-yellow-600 dark:border-yellow-400' },
                { bg: 'bg-blue-400 dark:bg-pink-400', border: 'border-blue-600 dark:border-pink-400' },
                { bg: 'bg-red-400 dark:bg-yellow-400', border: 'border-red-600 dark:border-yellow-400' },
                { bg: 'bg-green-400', border: 'border-green-600 dark:border-green-400' },
              ];
              const colors = colorOptions[index % colorOptions.length];
              
              return (
              <motion.div
                key={project.id}
                className={`bg-[#FFF8E7] dark:bg-[#2d2d44] border-4 ${colors.border} shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] dark:shadow-[6px_6px_0px_0px_currentColor] hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:hover:shadow-[8px_8px_0px_0px_currentColor] transition-all hover:translate-x-[-2px] hover:translate-y-[-2px] cursor-pointer group duration-500`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                onClick={() => openModal(project)}
              >
                {/* Project Title Bar */}
                <div className={`${colors.bg} border-b-4 ${colors.border} px-4 py-3 flex items-center justify-between transition-all duration-500`}>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-black dark:shadow-[0_0_10px_currentColor] transition-all duration-500"></div>
                    <span className="font-black uppercase tracking-tight text-sm text-black">PROJECT_{String(index + 1).padStart(2, '0')}</span>
                  </div>
                  <div className="flex gap-1">
                    <div className="w-5 h-5 bg-white dark:bg-black border-2 border-black dark:border-white flex items-center justify-center transition-all duration-500">
                      <span className="text-xs font-black dark:text-white">?</span>
                    </div>
                  </div>
                </div>

                {/* Project Content */}
                <div className="p-6">
                  <h3 className="text-2xl font-black uppercase mb-2 leading-tight group-hover:text-blue-600 dark:group-hover:drop-shadow-[0_0_10px_currentColor] transition-all duration-500 dark:text-white">
                    {project.title}
                  </h3>
                  <p className="text-gray-800 dark:text-gray-300 font-bold mb-4 transition-colors duration-500">{project.shortDescription}</p>
                  
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal(project);
                      }}
                      className={`bg-blue-400 dark:${colors.bg} border-3 border-black px-4 py-2 font-black uppercase text-sm hover:bg-blue-500 dark:hover:shadow-[0_0_15px_currentColor] transition-all inline-flex items-center gap-2 text-black duration-500`}
                    >
                      VIEW <span className="text-lg">→</span>
                    </button>
                    <div className={`w-8 h-8 ${colors.bg} border-2 border-black flex items-center justify-center font-black dark:shadow-[0_0_10px_currentColor] transition-all duration-500`}>
                      ✓
                    </div>
                  </div>
                </div>
              </motion.div>
            )})}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
        >
          <div className="bg-[#FFF8E7] dark:bg-[#2d2d44] border-4 border-black dark:border-green-400 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[8px_8px_0px_0px_rgba(0,255,136,0.5)] inline-block transition-all duration-500">
            <div className="bg-gradient-to-r from-blue-400 to-purple-500 dark:from-cyan-400 dark:to-purple-500 border-b-4 border-black dark:border-green-400 px-6 py-3 transition-all duration-500">
              <span className="font-black text-xl uppercase tracking-tight text-black">Ready to Connect?</span>
            </div>
            <div className="p-8">
              <p className="text-xl font-bold mb-6 text-gray-800 dark:text-green-400 transition-colors duration-500">
                Let's build something amazing together
              </p>
              <Link 
                to="/05_contact"
                className="inline-block bg-yellow-400 hover:bg-yellow-500 border-4 border-black px-8 py-4 font-black text-2xl uppercase shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_20px_rgba(255,255,0,0.6)] hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:hover:shadow-[0_0_30px_rgba(255,255,0,0.8)] transition-all hover:translate-x-[-2px] hover:translate-y-[-2px] text-black duration-500"
              >
                GET IN TOUCH →
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Quick Links Footer */}
        <motion.div
          className="mt-12 flex justify-center gap-4 flex-wrap"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          {[
            { label: "About", to: "/02_about", color: "bg-orange-400 dark:bg-pink-400", borderColor: "border-orange-600 dark:border-pink-400" },
            { label: "Work", to: "/03_work-experience", color: "bg-green-400", borderColor: "border-green-600 dark:border-green-400" },
            { label: "Projects", to: "/04_projects", color: "bg-blue-400 dark:bg-cyan-400", borderColor: "border-blue-600 dark:border-cyan-400" }
          ].map((link) => (
            <Link
              key={link.label}
              to={link.to}
              className={`${link.color} border-4 ${link.borderColor} px-6 py-3 font-black uppercase hover:scale-105 transition-transform shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[4px_4px_0px_0px_currentColor] dark:hover:shadow-[0_0_20px_currentColor] text-black duration-500`}
            >
              {link.label}
            </Link>
          ))}
        </motion.div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-20 right-10 w-16 h-16 bg-yellow-400 border-4 border-yellow-600 dark:border-yellow-400 rotate-12 hidden lg:block dark:shadow-[0_0_30px_rgba(255,255,0,0.6)] dark:animate-pulse transition-all duration-500"></div>
      <div className="absolute bottom-20 left-10 w-20 h-20 bg-blue-400 dark:bg-cyan-400 border-4 border-blue-600 dark:border-cyan-400 rounded-full hidden lg:block dark:shadow-[0_0_30px_rgba(0,255,255,0.6)] dark:animate-pulse transition-all duration-500"></div>
      <div className="absolute top-1/2 right-20 w-12 h-12 bg-red-400 dark:bg-pink-400 border-4 border-red-600 dark:border-pink-400 -rotate-12 hidden lg:block dark:shadow-[0_0_30px_rgba(255,0,255,0.6)] dark:animate-pulse transition-all duration-500"></div>

      {/* Modal */}
      <Modal 
        isOpen={isModalOpen}
        onClose={closeModal}
        title={selectedProject?.title}
        description={selectedProject?.longDescription}
        images={
          selectedProject?.images?.length > 1
            ? selectedProject.images.slice(1).map((img) => ({
                src: img.src,
                alt: img.alt || `Image for ${selectedProject.title}`,
                caption: img.caption,
              }))
            : []
        }
      />
    </div>
  );
};

export default HomeRetroDual;